import { useQuery } from "@tanstack/react-query";
import { useAuthSession } from "./useAuthSession";

interface Booking {
  classId?: string;
  status?: string;
  paymentStatus?: string;
}

async function fetchUserBookings(userId?: string): Promise<Set<string>> {
  try {
    // Don't make request if no userId
    if (!userId) {
      console.log("❌ No user ID found, skipping booking fetch");
      return new Set();
    }

    console.log("✅ Found userId, proceeding with booking fetch:", userId);

    const response = await fetch(`/api/bookings/my-bookings/${userId}`, {
      credentials: "include",
    });

    if (response.ok) {
      const { bookings } = await response.json();
      console.log("📥 User bookings response:", bookings);
      const bookedClassIds = new Set<string>();

      bookings?.forEach((booking: Booking) => {
        console.log(`  Booking: classId=${booking.classId}, status=${booking.status}, paymentStatus=${booking.paymentStatus}`);
        // Only consider a class as "booked" if it's confirmed AND payment is successful
        if (
          booking.classId &&
          booking.status === "confirmed" &&
          booking.paymentStatus === "paid"
        ) {
          bookedClassIds.add(booking.classId);
          console.log(`    ✅ Added ${booking.classId} to booked set`);
        }
      });

      console.log("✅ User booked classes IDs:", Array.from(bookedClassIds));
      return bookedClassIds;
    } else if (response.status === 401) {
      // User not logged in - this is expected, don't log error
      console.log("User not authenticated, skipping booking fetch");
      return new Set();
    } else {
      console.error("Failed to fetch bookings:", response.status);
      return new Set();
    }
  } catch (error) {
    console.error("Error fetching user bookings:", error);
    return new Set();
  }
}

export function useUserBookings() {
  const { user, isLoading } = useAuthSession();
  const userId = user?.id;

  // console.log("useUserBookings - userId:", userId, "enabled:", !!userId);

  return useQuery({
    queryKey: ["userBookings", userId], // Include userId in query key
    queryFn: () => fetchUserBookings(userId),
    enabled: !!userId && !isLoading, // Only run query if userId exists and auth is loaded
    staleTime: 60 * 1000, // 1 minute
    retry: false, // Don't retry failed requests
  });
}
